import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, useTheme } from '@mui/material'
import { toast } from 'react-toastify'

import { exportQuizzesAuditReport } from '../../../utils/auditing-export-utilities'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import AuditPageStatsFilterBar from '../AuditPageStatsFilterBar'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import CustomTable from '../../common/custom-table/CustomTable'
import LineBarGraph from '../../common/LineBarGraph'
import { Icons } from '../../../utils/utilities'
import api from '../../../service/api'


const columns = [
    { id: 1, field: "index", index: true, name: 'Sl. No.', align: "center", padding: "10px 20px" },
    { id: 2, field: "faculty_name", filterable: true, linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.created_by}?legacy=${row.legacy_system_data}`, renderCell: row => row?.faculty_name?.toLowerCase() || row?.created_by, cellStyles: { textTransform: "capitalize" }, name: "Faculty", width: "150px", padding: "10px 20px" },
    { id: 3, field: "crated_by", filterable: true, linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.created_by}?legacy=${row.legacy_system_data}`, renderCell: row => row?.created_by, name: 'AUID', padding: "10px 20px" },
    { id: 6, field: "department", filterable: true, renderCell: row => `${row.institute} - ${row.department?.toLowerCase()}`, cellStyles: { textTransform: "capitalize" }, name: "Department", width: "150px", padding: "10px 20px" },
    { id: 4, field: "count", dataType: "number", sortable: true, filterable: true, align: "center", style: { align: "center" }, name: "Quiz Instances", width: "150px", padding: "10px 20px" },
    { id: 5, field: "attempts", dataType: "number", sortable: true, filterable: true, align: "center", style: { align: "center" }, name: "Attempts", width: "150px", padding: "10px 20px" },
]

const QuizzesStats = () => {

    const [searchParams] = useSearchParams( { sortByDept: "0", searchText: "", month: "", selectedDepartment: "all", selectedInstitute: "all", view: "tabular", sortByName: "0", sortByAttemptsCount: "0", sortByQuizzesCount: "0" } )
    const month = searchParams.get( 'month' ) || ""
    const view = searchParams.get( 'view' ) || "tabular"
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"
    const selectedDepartment = searchParams.get( 'selectedDepartment' ) || "all"


    const [loading, setLoading] = useState( true )
    const [graphData, setGraphData] = useState( [] )
    const [statsError, setStatsError] = useState( null )
    const [graphError, setGraphError] = useState( null )
    const [facultyStats, setFacultyStats] = useState( [] )
    const [last12MonthsStats, setLast12MonthsStats] = useState( null )
    const [loadingGraphData, setLoadingGraphData] = useState( true )
    const [stats, setStats] = useState( [] )
    const [loadingZeroQuizzesFaculties, setLoadingZeroQuizzesFaculties] = useState( false )
    const [showFacultiesWithZeroQuizzes, setShowFacultiesWithZeroQuizzes] = useState( false )

    const { layout, palette } = useTheme()

    const filterFunc = item => {
        if ( selectedDepartment !== "all" ) {
            const [department_id, legacy_department_id] = selectedDepartment?.split( "-" )
            return ( item.legacy_system_data && +legacy_department_id === +item?.department_id ) || ( !item.legacy_system_data && +department_id === +item?.department_id )
        }
        return true
    }

    const handleShowFacultiesWithZeroQuizzesChange = async e => {
        if ( e.target.checked ) {
            setLoadingZeroQuizzesFaculties( true )
            try {
                const queries = {
                    institute: selectedInstitute !== "all" ? selectedInstitute : "",
                    department: selectedDepartment !== "all" ? selectedDepartment : ""
                }
                const { data: { data: facultiesList } } = await api.audit.fetchFacultiesUnderAuditor( queries )
                const facultiesIdWithExams = facultyStats.map( i => i.created_by )
                const facultiesIdWithoutExams = facultiesList.filter( i => !facultiesIdWithExams.includes( i.session_id ) ).map( i => {
                    return {
                        count: 0,
                        attempts: 0,
                        faculty_name: i.employee_name,
                        department: i.branch_name,
                        department_id: i.branch_id,
                        institute: i.institute_name_short,
                        created_by: i.session_id,
                    }
                } )
                setStats( [...facultiesIdWithoutExams, ...facultyStats] )
                setShowFacultiesWithZeroQuizzes( true )
            } catch ( err ) {
                console.log( err )
                toast( "Oops! Something went wrong while fetching faculties with no exams." )
            }
        } else {
            setStats( facultyStats )
            setShowFacultiesWithZeroQuizzes( false )
        }
        setLoadingZeroQuizzesFaculties( false )
    }

    const getData = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data: { facultyStats, last12MonthsStats = {} } } } = await api.audit.getQuizzesStats( { month, institute: selectedInstitute !== "all" ? selectedInstitute : "" } )
            setFacultyStats( facultyStats )
            setStats( facultyStats )
            setLast12MonthsStats( Object.keys( last12MonthsStats ).length > 0 ? last12MonthsStats : null )
            setStatsError( null )
        } catch ( err ) {
            setStatsError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching the quizzes audit data." } )
        } finally {
            setLoading( false )
        }
    }, [month, selectedInstitute] )


    const getGraphData = useCallback( async () => {
        try {
            setLoadingGraphData( true )
            const institute = selectedInstitute !== "all" ? selectedInstitute : ""
            const department = selectedDepartment !== "all" ? selectedDepartment : ""
            const { data: graphDetails } = await api.audit.getQuizGraphData( { month, institute, department } )
            setGraphData( graphDetails.data )
            setGraphError( null )
        } catch ( err ) {
            setGraphError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching the quizzes graph data." } )
        } finally {
            setLoadingGraphData( false )
        }
    }, [month, selectedInstitute, selectedDepartment] )

    useEffect( () => {
        setShowFacultiesWithZeroQuizzes( false )
    }, [month] )

    useEffect( () => {
        getData()
    }, [getData] )

    useEffect( () => {
        getGraphData()
    }, [getGraphData] )

    const filtered = stats.filter( filterFunc )

    return (
        <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: layout.cardsGap }}>

            {/* Filters */}
            <AuditPageStatsFilterBar handleShowFacultyWithZeroResourceChange={handleShowFacultiesWithZeroQuizzesChange} showzeroassignmentsfaculties={showFacultiesWithZeroQuizzes} loadingZeroQuizzesFaculties={loadingZeroQuizzesFaculties} resource="quizzes" listResourceBtnLabel="View Quizzes" title="Quizzes statistics" />

            {/* Table */}
            {!statsError && !loading && view === 'tabular' && <CustomTable
                globalSearch="Search by faculty name or employee code..."
                hasFilters={selectedDepartment !== "all" || selectedInstitute !== "all"}
                pageSize={50}
                columns={columns}
                data={filtered}
                tableStyles={{
                    maxHeight: "450px"
                }}
                customActions={[
                    {
                        type: "button",
                        label: "Export",
                        startIcon: Icons.default.Publish,
                        onClick: ( rows ) => () => exportQuizzesAuditReport( rows, last12MonthsStats, `${month || "complete"}-${selectedInstitute !== "all" ? selectedInstitute + "-" : ""}${selectedDepartment !== "all" ? selectedDepartment : "alldept"}-quizzes-audit-report` )
                    }
                ]}
            />}

            {/* Retry */}
            {statsError && <TryAgainMessageBlock err={statsError.message} loading={loading} getData={getData} code={statsError.code} />}

            {/* Graph */}
            {
                !loading && !loadingGraphData && view !== 'tabular' && filtered.length > 0 &&
                <Box display="flex" color={palette.common.font} flexGrow={1} minHeight="400px">
                    {!graphError ? <LineBarGraph showMenuBar={false} barLegend='Quiz attempts' yAxisName="Quiz Instances" xAxisName="Date" y2AxisName="Attempts" lineLegend='Quiz instances created' barValuesKey="attempts" barData={graphData} dateformat={"%Y-%m-%d"} xAxisValuesKey={'date'} lineValuesKey={'quizzes'} lineData={graphData} />
                        : <TryAgainMessageBlock err={graphError.message} loading={loadingGraphData} getData={getGraphData} code={graphError.code} />}
                </Box>
            }

            {/* Loading */}
            {( loading || loadingGraphData ) && !statsError && !graphError && <DataLoadingSpinner padding='0' waitingMessage="Loading quizzes audit report please..." />}
        </Box >
    )
}

export default QuizzesStats
